/**
 * EURO IMAGE picks.
 *
 *   euro-picks__tabs  -> the tab strip
 *   euro-picks__main  -> one slide per package
 *
 * Both breakpoints pair those two with Swiper `thumbs`. Desktop then previews
 * an option on hover. Mobile pins the card and scrubs package options as the
 * visitor scrolls, so the next module does not arrive until every beat is seen.
 */
class EuroPicks extends HTMLElement {
  connectedCallback() {
    this.tabsEl = this.querySelector('[data-picks-tabs]');
    this.mainEl = this.querySelector('[data-picks-main]');
    this.tabButtons = Array.from(this.querySelectorAll('[data-picks-index]'));
    this.mq = window.matchMedia('(min-width: 1200px)');

    // Option preview, desktop only: hovering or focusing an option swaps that
    // card's image layer, its includes and the highlight. Delegated from the
    // host, so one pair of listeners covers every card.
    this.onOptionOver = (event) => this.previewOption(event);
    this.addEventListener('mouseover', this.onOptionOver);
    this.addEventListener('focusin', this.onOptionOver);

    this.onChange = () => this.sync();
    this.onResize = () => {
      if (this.mq.matches) this.syncViewportHeight();
      else {
        this.layoutMobileTimeline();
        this.syncMobileTimeline();
      }
    };
    this.onTabClick = (event) => {
      const index = Number(event.currentTarget.dataset.picksIndex);
      if (Number.isNaN(index)) return;
      this.goTo(index);
    };
    this.onMobileScroll = () => this.syncMobileTimeline();
    this.onMobileRowClick = (event) => this.handleMobileRowClick(event);

    this.mq.addEventListener('change', this.onChange);
    window.addEventListener('resize', this.onResize);

    this.initializeActiveOptions();
    this.whenReady(() => this.sync());
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.onChange);
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('mouseover', this.onOptionOver);
    this.removeEventListener('focusin', this.onOptionOver);
    clearInterval(this.readyTimer);
    this.destroy();
  }

  sync() {
    this.destroy();
    this.init();
  }

  whenReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }
    this.readyTimer = setInterval(() => {
      if (!window.Swiper) return;
      clearInterval(this.readyTimer);
      callback();
    }, 30);
  }

  init() {
    if (this.mainSwiper || !this.tabsEl || !this.mainEl || !window.Swiper) return;
    if (this.mq.matches) this.initDesktop();
    else this.initMobile();
  }

  initDesktop() {
    this.tabsSwiper = new window.Swiper(this.tabsEl, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
    });

    this.mainSwiper = new window.Swiper(this.mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 500,
      watchOverflow: true,
      thumbs: {
        swiper: this.tabsSwiper,
        autoScrollOffset: 1,
      },
      on: {
        slideChange: () => this.syncViewportHeight(),
      },
    });

    this.tabsSwiper.update();
    this.mainSwiper.update();
    this.syncViewportHeight();
  }

  initMobile() {
    this.tabsSwiper = new window.Swiper(this.tabsEl, {
      slidesPerView: 'auto',
      spaceBetween: this.mobileTabGap(),
      watchSlidesProgress: true,
      watchOverflow: true,
      allowTouchMove: false,
    });

    this.mainSwiper = new window.Swiper(this.mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 500,
      watchOverflow: true,
      allowTouchMove: false,
      on: {
        slideChange: (swiper) => {
          this.setActiveTab(swiper.activeIndex);
          if (!this.timelineDriving) this.syncMobileCard(swiper.activeIndex);
        },
      },
    });

    this.mobilePackageIndex = 0;
    this.setActiveTab(0);
    this.tabsSwiper.update();
    this.mainSwiper.update();

    this.tabButtons.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
    });
    this.addEventListener('click', this.onMobileRowClick);
    window.addEventListener('scroll', this.onMobileScroll, { passive: true });

    this.layoutMobileTimeline();
    this.syncMobileTimeline();
  }

  setActiveTab(index) {
    this.querySelectorAll('.euro-picks__tab-slide').forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });
  }

  goTo(index) {
    if (!this.mq.matches) {
      this.selectMobilePackage(index);
      return;
    }
    this.setActiveTab(index);
    this.mainSwiper?.slideTo(index);
    this.tabsSwiper?.slideTo(index);
  }

  selectMobilePackage(index) {
    this.mobilePackageIndex = index;
    this.setActiveTab(index);
    this.timelineDriving = true;
    this.mainSwiper?.slideTo(index, 0);
    this.timelineDriving = false;
    this.layoutMobileTimeline();
    this.jumpToMobileState(0);
  }

  mobileCardAt(index) {
    const slide = this.mainSwiper?.slides?.[index];
    return slide?.querySelector('[data-picks-mobile-card]') || null;
  }

  mobileOptions(card) {
    return Array.from(card?.querySelectorAll('[data-picks-option]') || []).map(
      (option) => option.dataset.picksOption
    );
  }

  setMobileOption(card, index) {
    if (!card || !index) return;
    card.dataset.activeOption = index;
    card.querySelectorAll('[data-picks-option]').forEach((option) => {
      option.classList.toggle('is-active', option.dataset.picksOption === index);
    });
    card.querySelectorAll('[data-picks-includes]').forEach((copy) => {
      copy.classList.toggle('is-active', copy.dataset.picksIncludes === index);
    });
  }

  syncMobileCard(index) {
    const card = this.mobileCardAt(index);
    if (!card) return;
    const options = this.mobileOptions(card);
    if (options.length === 0) return;
    const next = card.dataset.picksDefault || options[0];
    this.setMobileOption(card, options.includes(next) ? next : options[0]);
  }

  mobileTabGap() {
    return (32 / 375) * window.innerWidth;
  }

  collectMobileStates(packageIndex) {
    return this.mobileOptions(this.mobileCardAt(packageIndex)).map((option) => ({ option }));
  }

  layoutMobileTimeline() {
    const packageIndex = this.mobilePackageIndex || 0;
    this.mobileStates = this.collectMobileStates(packageIndex);
    const count = Math.max(this.mobileStates.length, 1);
    this.mobileStep = Math.round(window.innerHeight * 0.9);
    this.style.setProperty(
      '--euro-picks-track',
      `${count * this.mobileStep + window.innerHeight}px`
    );
    if (this.tabsSwiper) {
      this.tabsSwiper.params.spaceBetween = this.mobileTabGap();
      this.tabsSwiper.update();
    }
  }

  mobileStateFromScroll() {
    if (!this.mobileStates?.length) return 0;
    const start = this.getBoundingClientRect().top + window.scrollY;
    const scrolled = Math.min(
      Math.max(window.scrollY - start, 0),
      this.mobileStates.length * this.mobileStep
    );
    return Math.min(
      this.mobileStates.length - 1,
      Math.floor(scrolled / this.mobileStep)
    );
  }

  syncMobileTimeline() {
    if (this.mq.matches || !this.mobileStates?.length) return;
    this.applyMobileState(this.mobileStateFromScroll());
  }

  applyMobileState(index) {
    if (index === this.timelineIndex) return;
    const state = this.mobileStates[index];
    if (!state) return;
    this.timelineIndex = index;
    this.setMobileOption(this.mobileCardAt(this.mobilePackageIndex || 0), state.option);
  }

  jumpToMobileState(index) {
    const start = window.scrollY + this.getBoundingClientRect().top;
    window.scrollTo(0, start + index * this.mobileStep + 1);
    this.timelineIndex = -1;
    this.applyMobileState(index);
  }

  handleMobileRowClick(event) {
    const option = event.target.closest?.('[data-picks-option]');
    if (!option || !this.contains(option)) return;
    const card = option.closest('[data-picks-mobile-card]');
    if (!card || !this.mobileStates?.length) return;
    const stateIndex = this.mobileStates.findIndex(
      (state) => state.option === option.dataset.picksOption
    );
    if (stateIndex < 0 || stateIndex === this.timelineIndex) return;
    event.preventDefault();
    this.jumpToMobileState(stateIndex);
  }

  /**
   * Desktop only: highlights one of a card's three option buttons and swaps the
   * card's picture and copy to match.
   */
  setActiveOption(card, index) {
    if (!card || !index) return;
    card.dataset.activeOption = index;
    card.querySelectorAll('[data-picks-option]').forEach((option) => {
      option.classList.toggle('is-featured', option.dataset.picksOption === index);
    });
    this.syncViewportHeight();
  }

  /**
   * Sets the option each desktop card starts on, before any hovering.
   *
   * Liquid renders `data-picks-default` from the block's "Default option"
   * setting, but that slot can be empty — an option with no label and no price
   * renders no button — in which case fall back to the first option the card
   * actually has, so the card never comes up with no image and nothing
   * highlighted.
   */
  initializeActiveOptions() {
    this.querySelectorAll('.euro-picks__card:not([data-picks-mobile-card])').forEach((card) => {
      const options = Array.from(card.querySelectorAll('[data-picks-option]'));
      if (options.length === 0) return;
      const configured = card.dataset.picksDefault || '1';
      const known = options.some((option) => option.dataset.picksOption === configured);
      this.setActiveOption(card, known ? configured : options[0].dataset.picksOption);
    });
  }

  previewOption(event) {
    const option = event.target.closest?.('[data-picks-option]');
    if (!option || !this.contains(option)) return;
    const card = option.closest('[data-picks-card]');
    if (!card || card.hasAttribute('data-picks-mobile-card')) return;
    this.setActiveOption(card, option.dataset.picksOption);
  }

  /**
   * Keeps the carousel as tall as the card on screen.
   *
   * A card is as tall as the ratio of the option it shows, so the two packages
   * can differ in height. Swiper sizes the wrapper to the tallest slide, which
   * would leave blank space under the shorter card — and that grows whenever a
   * card is left on a taller option by the sticky preview. Clipping the wrapper
   * to the visible card keeps the section sized to what is on screen.
   */
  syncViewportHeight() {
    if (!this.mq.matches || !this.mainEl || !this.mainSwiper) return;
    const slide = this.mainSwiper.slides?.[this.mainSwiper.activeIndex];
    // Whichever card this breakpoint shows; the other one is display:none and
    // measures 0, so skip it.
    const cards = slide ? Array.from(slide.querySelectorAll('.euro-picks__card')) : [];
    const card = cards.find((el) => el.offsetHeight > 0);
    this.mainEl.style.height = card ? `${card.offsetHeight}px` : '';
  }

  unbindMobile() {
    this.tabButtons.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
    });
    this.removeEventListener('click', this.onMobileRowClick);
    window.removeEventListener('scroll', this.onMobileScroll);
    this.style.removeProperty('--euro-picks-track');
    this.mobileStates = null;
    this.mobilePackageIndex = 0;
    this.timelineIndex = -1;
    this.timelineDriving = false;
  }

  destroy() {
    this.unbindMobile();
    this.querySelectorAll('.euro-picks__tab-slide').forEach((slide) => {
      slide.classList.remove('is-active');
    });
    if (this.mainEl) this.mainEl.style.height = '';
    this.mainSwiper?.destroy(true, true);
    this.tabsSwiper?.destroy(true, true);
    this.mainSwiper = null;
    this.tabsSwiper = null;
  }
}

customElements.define('euro-picks', EuroPicks);
